/**
 * 瞬时失败要退避重试 —— 全仓唯一一份判断。
 *
 * <p>Infra 1.5 文档：「<b>429 限流、超时、5xx → 退避重试，不算断开</b>」。
 *
 * <p><b>此前是什么样。</b>{@code api.ts} 里确实有一个 {@code retryRequest}，但它只接在
 * {@code graphql()} 上，而全仓 GraphQL 调用是 0 处；真正跑业务的 {@code get/post/put/patch/delete}
 * 共 37 处调用，<b>一次都不重试</b>。更靠前的一道闸在响应拦截器里：除了 401 和 502，
 * 所有 HTTP 错误都被 {@code envelopeAsResolution()} 变成一个「status 200 + 错误信封」的
 * <b>成功</b> promise。所以就算把 503 写进重试名单也不会生效 —— 重试机制根本看不到它。
 *
 * <p><b>所以这一版把重试放在拦截器里，不放在调用方。</b>拦截器在决定「resolve 成信封」
 * 还是「reject」<b>之前</b>先退避重试；重试次数用尽了才落回原来那套。好处是调用方一行都不用改，
 * 对外契约（拿到的还是信封）也完全不变 —— 只是拿到它之前先悄悄试了几次。
 *
 * <p><b>为什么写请求不跟着一起重试。</b>「超时」和「502 / 504」在写请求上是<b>歧义</b>的：
 * 请求可能已经打到后端、后端也已经建好了商品，只是回程断了。这时候重试就是第二次创建。
 * 所以只有 <b>429</b> 和 <b>503</b> 才对写请求重试 —— 这两个码的语义是「我明确没有处理，
 * 过会儿再来」，不存在歧义。读请求（GET / HEAD / OPTIONS）本身幂等，整份名单都给。
 *
 * <p><b>方法认不出来时按写请求算。</b>判错的代价不对称：漏重试只是维持现状，
 * 多重试一次写请求可能给商家多建一个商品、多扣一次费。所以宁可少重试。
 */

/** 重试几次（不含首次请求本身）。 */
export const RETRY_MAX_ATTEMPTS = 3;

/** 退避的起点。 */
export const RETRY_BASE_MS = 1000;

/**
 * 退避的封顶。
 *
 * <p>30s 是 {@code errorHandling.ts} 里那份早就写好、却零调用的实现定的数，沿用它，
 * 免得仓库里出现第二个口径。
 */
export const RETRY_CAP_MS = 30000;

/** 语义是「我明确没处理，过会儿再来」—— 没有歧义，读写都能重试。 */
const RETRYABLE_ANY_METHOD = new Set([429, 503]);

/** 语义歧义 —— 只有幂等方法能重试。 */
const RETRYABLE_IDEMPOTENT_ONLY = new Set([500, 502, 504]);

/** HTTP 定义的幂等方法里，我们实际会发的这几个。 */
const IDEMPOTENT_METHODS = new Set(['get', 'head', 'options']);

/**
 * axios 在「压根没收到响应」时给的 code。
 *
 * <p>{@code ECONNABORTED} 是超时（axios 自己掐的），{@code ETIMEDOUT} 是系统层超时，
 * {@code ERR_NETWORK} 是断网 / DNS / CORS 预检失败这一类。
 */
const NO_RESPONSE_CODES = new Set(['ECONNABORTED', 'ETIMEDOUT', 'ERR_NETWORK']);

export interface RetrySignal {
  /** HTTP 状态码；压根没拿到响应时为 undefined。 */
  status?: number;
  /** 请求方法，大小写不敏感。 */
  method?: string;
  /** axios 的 error.code。 */
  code?: string;
  /** 是否拿到了响应。false = 超时 / 断网。 */
  hasResponse: boolean;
  /** 响应头里的 `Retry-After`，原样传进来。 */
  retryAfter?: string | null;
  /** 已经重试过几次。首次失败时是 0。 */
  attempt: number;
  /** 现在的时刻，用来算 HTTP-date 形式的 Retry-After。注入是为了测试可控。 */
  now?: number;
}

export interface RetryPlan {
  /** 等多久再发。 */
  delayMs: number;
  /** 这次重试是第几次（从 1 开始），只用于日志。 */
  attempt: number;
  /** 延迟是不是后端用 `Retry-After` 点名的。 */
  honoredRetryAfter: boolean;
}

/** 指数退避：1s、2s、4s、8s、16s，封顶 30s。 */
export function backoffDelay(attempt: number): number {
  const raw = RETRY_BASE_MS * Math.pow(2, Math.max(0, attempt));
  return Math.min(raw, RETRY_CAP_MS);
}

/**
 * 解析 `Retry-After`。
 *
 * <p>RFC 9110 允许两种写法：秒数（`"120"`）和 HTTP-date（`"Wed, 21 Oct 2026 07:28:00 GMT"`）。
 * 两种都认。解析不出来、是负数、或者后端给了个 `0`，都当作「没给」交回 null，让退避阶梯接手。
 */
export function parseRetryAfter(
  raw: string | null | undefined,
  now: number = Date.now()
): number | null {
  if (raw == null) return null;
  const text = String(raw).trim();
  if (text === '') return null;

  if (/^\d+$/.test(text)) {
    const seconds = Number(text);
    return seconds > 0 ? seconds * 1000 : null;
  }

  const at = Date.parse(text);
  if (Number.isNaN(at)) return null;
  const delta = at - now;
  return delta > 0 ? delta : null;
}

/**
 * 这一次失败该不该重试、等多久。不该重试就返回 null。
 *
 * <p>判定顺序：次数用尽 → 不重试；不在名单里 → 不重试；名单里但歧义、而这是写请求 → 不重试。
 * 都过了才算延迟。
 *
 * <p><b>后端说「过会儿再来」但那个「会儿」超过封顶时，不重试。</b>`Retry-After: 300` 的意思是
 * 五分钟内别来。挂着转圈等五分钟，比当场把失败交给调用方更糟 —— 那五分钟里商家什么都做不了，
 * 也不知道在等什么。这种就直接失败，让上层去决定怎么跟商家说。
 */
export function planRetry(signal: RetrySignal): RetryPlan | null {
  const { status, method, code, hasResponse, retryAfter, attempt } = signal;
  const now = signal.now ?? Date.now();

  if (attempt >= RETRY_MAX_ATTEMPTS) return null;

  const idempotent = IDEMPOTENT_METHODS.has((method ?? '').toLowerCase());

  // 压根没拿到响应 —— 超时 / 断网。歧义，只有幂等方法重试。
  if (!hasResponse) {
    const looksTransient = code == null || NO_RESPONSE_CODES.has(code);
    if (!looksTransient || !idempotent) return null;
    return { delayMs: backoffDelay(attempt), attempt: attempt + 1, honoredRetryAfter: false };
  }

  if (status == null) return null;

  const unambiguous = RETRYABLE_ANY_METHOD.has(status);
  const ambiguous = RETRYABLE_IDEMPOTENT_ONLY.has(status);
  if (!unambiguous && !ambiguous) return null;
  if (ambiguous && !idempotent) return null;

  const named = parseRetryAfter(retryAfter, now);
  if (named != null) {
    if (named > RETRY_CAP_MS) return null;
    return { delayMs: named, attempt: attempt + 1, honoredRetryAfter: true };
  }

  return { delayMs: backoffDelay(attempt), attempt: attempt + 1, honoredRetryAfter: false };
}
